<template>
  <div class="min-h-screen bg-gray-50 dark:bg-slate-950">

    <!-- ── MOBILE: indigo gradient hero ── -->
    <div class="md:hidden bg-gradient-to-br from-indigo-600 via-indigo-500 to-violet-500 px-4 pt-5 pb-8">
      <!-- Back button (own customer only) -->
      <a v-if="props.is_own_customer" href="/customers"
        class="inline-flex items-center gap-1.5 text-white/80 text-xs font-semibold mb-4">
        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"/>
        </svg>
        Back to Customers
      </a>
      <div class="flex items-center gap-3 mb-5">
        <div class="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center text-white font-bold text-xl shrink-0">
          {{ props.customer.name.charAt(0).toUpperCase() }}
        </div>
        <div class="flex-1 min-w-0">
          <h1 class="text-lg font-bold text-white truncate">{{ props.customer.name }}</h1>
          <p class="text-indigo-200 text-xs truncate">{{ props.customer.shop_name }}</p>
          <p class="text-indigo-300/80 text-[10px] mt-0.5 truncate">{{ props.customer.business_name }}</p>
        </div>
      </div>
      <div v-if="!dataLoading" class="grid grid-cols-3 gap-2">
        <div class="bg-white/15 backdrop-blur-sm rounded-2xl px-3 py-3 text-center">
          <p class="text-[9px] font-bold uppercase tracking-widest mb-1"
            :class="props.is_own_customer ? 'text-emerald-200' : 'text-red-300'">
            {{ props.is_own_customer ? 'Receivable' : 'Payable' }}
          </p>
          <p class="text-[10px] min-[360px]:text-xs min-[400px]:text-sm font-bold text-white whitespace-nowrap">Rs {{ formatNum(financials?.receivable) }}</p>
        </div>
        <div class="bg-white/15 backdrop-blur-sm rounded-2xl px-3 py-3 text-center">
          <p class="text-[9px] font-bold uppercase tracking-widest mb-1"
            :class="props.is_own_customer ? 'text-red-300' : 'text-emerald-200'">
            {{ props.is_own_customer ? 'Payable' : 'I\'ll Get' }}
          </p>
          <p class="text-[10px] min-[360px]:text-xs min-[400px]:text-sm font-bold text-white whitespace-nowrap">Rs {{ formatNum(financials?.payable) }}</p>
        </div>
        <div class="bg-white/15 backdrop-blur-sm rounded-2xl px-3 py-3 text-center">
          <p class="text-[9px] font-bold uppercase tracking-widest mb-1"
            :class="financials?.balance >= 0 ? 'text-yellow-200' : 'text-orange-300'">Net</p>
          <p class="text-[10px] min-[360px]:text-xs min-[400px]:text-sm font-bold text-white whitespace-nowrap">Rs {{ formatNum(Math.abs(financials?.balance)) }}</p>
        </div>
      </div>
      <div v-else class="grid grid-cols-3 gap-2">
        <div v-for="i in 3" :key="i" class="bg-white/15 rounded-2xl h-14 animate-pulse"></div>
      </div>
    </div>

    <!-- ── DESKTOP: white top bar ── -->
    <div class="hidden md:flex bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800 px-4 py-3 items-center gap-3">
      <a v-if="props.is_own_customer" href="/customers"
        class="flex items-center justify-center w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 transition shrink-0">
        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"/>
        </svg>
      </a>
      <div class="w-9 h-9 rounded-xl bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center text-indigo-600 dark:text-indigo-400 font-bold text-base shrink-0">
        {{ props.customer.name.charAt(0).toUpperCase() }}
      </div>
      <div class="flex-1 min-w-0">
        <h1 class="text-sm font-bold text-slate-800 dark:text-white truncate">{{ props.customer.name }}</h1>
        <p class="text-xs text-slate-400 truncate">{{ props.customer.shop_name }}</p>
        <p class="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5 truncate">
          <span class="font-medium text-slate-500 dark:text-slate-400">{{ props.customer.business_name }}</span>
          <span class="mx-1.5 text-slate-300 dark:text-slate-600">·</span>
          <span>Full transaction history, invoices &amp; payments</span>
        </p>
      </div>
    </div>

    <!-- ── MOBILE: white card pulls up over hero / DESKTOP: plain content wrapper ── -->
    <div class="bg-gray-50 dark:bg-slate-950 rounded-t-3xl -mt-4 md:rounded-none md:mt-0 px-4 pt-4 md:pt-0 pb-8 space-y-4 md:max-w-2xl md:mx-auto md:py-5">

      <!-- Contact strip -->
      <div v-if="props.customer.phone_number || props.customer.city_name || props.customer.address"
        class="flex flex-wrap gap-2 pt-1 md:pt-0">
        <a v-if="props.customer.phone_number" :href="`tel:${props.customer.phone_number}`"
          class="flex items-center gap-1.5 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-600 dark:text-slate-300 shadow-sm">
          <svg class="w-3.5 h-3.5 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z"/>
          </svg>
          {{ props.customer.phone_number }}
        </a>
        <span v-if="props.customer.city_name"
          class="flex items-center gap-1.5 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-600 dark:text-slate-300 shadow-sm">
          <svg class="w-3.5 h-3.5 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z"/>
            <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"/>
          </svg>
          {{ props.customer.city_name }}
        </span>
        <span v-if="props.customer.address"
          class="flex items-center gap-1.5 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-600 dark:text-slate-300 shadow-sm">
          <svg class="w-3.5 h-3.5 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25"/>
          </svg>
          {{ props.customer.address }}
        </span>
      </div>

      <!-- ── DESKTOP ONLY: balance cards with tooltips ── -->
      <template v-if="dataLoading">
        <div class="hidden md:grid grid-cols-3 gap-3 md:mt-2">
          <div v-for="i in 3" :key="i" class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm p-3.5 space-y-2">
            <div class="h-2 w-16 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
            <div class="h-5 w-24 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
            <div class="h-2 w-12 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
          </div>
        </div>
      </template>
      <div v-else class="hidden md:grid grid-cols-3 gap-3 md:mt-2">
        <!-- Receivable (owner) / I Owe (customer) -->
        <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm p-3.5">
          <div class="flex items-center justify-between mb-1">
            <p class="text-[10px] font-semibold uppercase tracking-wide"
              :class="props.is_own_customer ? 'text-emerald-500' : 'text-red-400'">
              {{ props.is_own_customer ? 'Receivable' : 'Payable' }}
            </p>
            <div v-if="props.is_own_customer" class="relative group/tip">
              <svg class="w-3.5 h-3.5 text-slate-300 dark:text-slate-600 cursor-default" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"/>
              </svg>
              <div class="pointer-events-none absolute top-full right-0 mt-1.5 w-52 opacity-0 group-hover/tip:opacity-100 transition-opacity z-50">
                <div class="w-2 h-2 bg-white dark:bg-slate-800 border-l border-t border-slate-200 dark:border-slate-700 rotate-45 ml-auto mr-1.5 mb-[-1px]"></div>
                <div class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2.5 shadow-xl text-xs leading-relaxed">
                  <p class="font-semibold text-slate-700 dark:text-slate-200 mb-1.5">Receivable Formula</p>
                  <p class="text-slate-500 dark:text-slate-300">Opening Balance (To Receive)</p>
                  <p class="text-slate-500 dark:text-slate-300">+ Sale Invoices Total</p>
                  <p class="text-slate-500 dark:text-slate-300">− Received Payments</p>
                  <div class="border-t border-slate-200 dark:border-slate-600 mt-1.5 pt-1.5 text-emerald-500 font-medium">= Amount customer owes you</div>
                </div>
              </div>
            </div>
          </div>
          <p class="text-sm font-bold leading-tight whitespace-nowrap"
            :class="props.is_own_customer ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'">
            Rs {{ formatNum(financials?.receivable) }}
          </p>
          <p class="text-[10px] text-slate-400 mt-0.5">
            {{ props.is_own_customer ? 'Owed to you' : 'Payable to ' + props.customer.business_name }}
          </p>
        </div>
        <!-- Payable (owner) / I'll Get (customer) -->
        <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm p-3.5">
          <div class="flex items-center justify-between mb-1">
            <p class="text-[10px] font-semibold uppercase tracking-wide"
              :class="props.is_own_customer ? 'text-red-400' : 'text-emerald-500'">
              {{ props.is_own_customer ? 'Payable' : 'Receivable' }}
            </p>
            <div v-if="props.is_own_customer" class="relative group/tip">
              <svg class="w-3.5 h-3.5 text-slate-300 dark:text-slate-600 cursor-default" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"/>
              </svg>
              <div class="pointer-events-none absolute top-full right-0 mt-1.5 w-52 opacity-0 group-hover/tip:opacity-100 transition-opacity z-50">
                <div class="w-2 h-2 bg-white dark:bg-slate-800 border-l border-t border-slate-200 dark:border-slate-700 rotate-45 ml-auto mr-1.5 mb-[-1px]"></div>
                <div class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2.5 shadow-xl text-xs leading-relaxed">
                  <p class="font-semibold text-slate-700 dark:text-slate-200 mb-1.5">Payable Formula</p>
                  <p class="text-slate-500 dark:text-slate-300">Opening Balance (To Give)</p>
                  <p class="text-slate-500 dark:text-slate-300">+ Purchase Invoices Total</p>
                  <p class="text-slate-500 dark:text-slate-300">− Sent Payments</p>
                  <div class="border-t border-slate-200 dark:border-slate-600 mt-1.5 pt-1.5 text-red-500 font-medium">= Amount you owe customer</div>
                </div>
              </div>
            </div>
          </div>
          <p class="text-sm font-bold leading-tight whitespace-nowrap"
            :class="props.is_own_customer ? 'text-red-500 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'">
            Rs {{ formatNum(financials?.payable) }}
          </p>
          <p class="text-[10px] text-slate-400 mt-0.5">
            {{ props.is_own_customer ? 'You owe' : 'Receivable from ' + props.customer.business_name }}
          </p>
        </div>
        <!-- Net Balance -->
        <div class="rounded-2xl border shadow-sm p-3.5"
          :class="financials?.balance >= 0
            ? 'bg-emerald-50 dark:bg-emerald-950/50 border-emerald-100 dark:border-emerald-900'
            : 'bg-red-50 dark:bg-red-950/50 border-red-100 dark:border-red-900'">
          <div class="flex items-center justify-between mb-1">
            <p class="text-[10px] font-semibold uppercase tracking-wide"
              :class="financials?.balance >= 0 ? 'text-emerald-500' : 'text-red-400'">Net Balance</p>
            <div v-if="props.is_own_customer" class="relative group/tip">
              <svg class="w-3.5 h-3.5 text-slate-300 dark:text-slate-600 cursor-default" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"/>
              </svg>
              <div class="pointer-events-none absolute top-full right-0 mt-1.5 w-52 opacity-0 group-hover/tip:opacity-100 transition-opacity z-50">
                <div class="w-2 h-2 bg-white dark:bg-slate-800 border-l border-t border-slate-200 dark:border-slate-700 rotate-45 ml-auto mr-1.5 mb-[-1px]"></div>
                <div class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2.5 shadow-xl text-xs leading-relaxed">
                  <p class="font-semibold text-slate-700 dark:text-slate-200 mb-1.5">Balance Formula</p>
                  <p class="text-slate-500 dark:text-slate-300">Receivable − Payable</p>
                  <div class="border-t border-slate-200 dark:border-slate-600 mt-1.5 pt-1.5 text-slate-500 dark:text-slate-300 font-medium">
                    <span class="text-emerald-500">Green</span> = you are owed money<br>
                    <span class="text-red-500">Red</span> = you owe money
                  </div>
                </div>
              </div>
            </div>
          </div>
          <p class="text-sm font-bold leading-tight whitespace-nowrap"
            :class="financials?.balance >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'">
            Rs {{ formatNum(Math.abs(financials?.balance)) }}
          </p>
          <p class="text-[10px] mt-0.5"
            :class="financials?.balance >= 0 ? 'text-emerald-500' : 'text-red-400'">
            {{ props.is_own_customer
                ? (financials?.balance >= 0 ? 'Your favour' : 'Their favour')
                : (financials?.balance >= 0 ? 'I need to pay' : 'I will receive') }}
          </p>
        </div>
      </div>

      <!-- Ledger skeleton -->
      <template v-if="dataLoading">
        <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm overflow-hidden">
          <div class="px-4 py-3 border-b border-slate-100 dark:border-slate-800 space-y-1.5">
            <div class="h-3 w-36 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
            <div class="h-2.5 w-24 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
          </div>
          <div v-for="i in 6" :key="i" class="flex items-center gap-3 px-4 py-3.5 border-b border-slate-50 dark:border-slate-800/50">
            <div class="w-9 h-9 rounded-xl bg-slate-100 dark:bg-slate-800 animate-pulse shrink-0"></div>
            <div class="flex-1 space-y-1.5">
              <div class="h-3 w-32 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
              <div class="h-2.5 w-20 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
            </div>
            <div class="h-3 w-16 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
          </div>
        </div>
      </template>

      <!-- Summary note -->
      <p v-if="!dataLoading && financials?.balance !== 0" class="text-xs text-slate-500 dark:text-slate-400 italic px-1">
        <template v-if="financials?.balance > 0">
          <span class="font-semibold not-italic text-slate-600 dark:text-slate-300">{{ props.customer.name }}</span>
          has to pay
          <span class="font-semibold not-italic text-emerald-600 dark:text-emerald-400">Rs {{ formatNum(financials?.balance) }}</span>
          to
          <span class="font-semibold not-italic text-slate-600 dark:text-slate-300">{{ props.customer.business_name }}</span>.
        </template>
        <template v-else>
          <span class="font-semibold not-italic text-slate-600 dark:text-slate-300">{{ props.customer.business_name }}</span>
          has to pay
          <span class="font-semibold not-italic text-red-500 dark:text-red-400">Rs {{ formatNum(Math.abs(financials?.balance)) }}</span>
          to
          <span class="font-semibold not-italic text-slate-600 dark:text-slate-300">{{ props.customer.name }}</span>.
        </template>
      </p>

      <!-- Ledger -->
      <div v-if="!dataLoading" class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm overflow-hidden">

        <!-- Header -->
        <div class="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-800">
          <div>
            <p class="text-sm font-semibold text-slate-800 dark:text-white">Transaction History</p>
            <p class="text-xs text-slate-400">{{ ledger.length }} transaction{{ ledger.length !== 1 ? 's' : '' }}</p>
          </div>
        </div>

        <!-- Transaction entries -->
        <template v-if="ledger.length > 0">
          <template v-for="entry in ledger" :key="entry.id">

            <!-- Row -->
            <div @click="toggleExpand(entry.id)"
              class="flex items-center gap-3 px-4 py-3.5 border-b border-slate-50 dark:border-slate-800/80 cursor-pointer select-none transition-colors"
              :class="expandedId === entry.id
                ? 'bg-slate-50 dark:bg-slate-800/50'
                : 'hover:bg-slate-50/70 dark:hover:bg-slate-800/30'">

              <!-- Icon -->
              <div class="w-9 h-9 rounded-xl shrink-0 flex items-center justify-center"
                :class="entry.kind === 'invoice'
                  ? (entry.action === 'sold' ? 'bg-emerald-100 dark:bg-emerald-950' : 'bg-blue-100 dark:bg-blue-950')
                  : (entry.action === 'received' ? 'bg-emerald-100 dark:bg-emerald-950' : 'bg-red-100 dark:bg-red-950')">
                <svg v-if="entry.kind === 'invoice'" class="w-4 h-4"
                  :class="entry.action === 'sold' ? 'text-emerald-600 dark:text-emerald-400' : 'text-blue-600 dark:text-blue-400'"
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z"/>
                </svg>
                <svg v-else class="w-4 h-4"
                  :class="entry.action === 'received' ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'"
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75"/>
                </svg>
              </div>

              <!-- Info -->
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-1.5 flex-wrap">
                  <span class="text-sm font-semibold text-slate-800 dark:text-white">
                    {{ entry.kind === 'invoice' ? `Invoice #${entry.invoice_number}` : 'Payment' }}
                  </span>
                  <span class="text-[10px] font-bold px-1.5 py-0.5 rounded-md uppercase tracking-wide"
                    :class="entry.kind === 'invoice'
                      ? (entry.action === 'sold'     ? 'bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-400'
                                                     : 'bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-400')
                      : (entry.action === 'received' ? 'bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-400'
                                                     : 'bg-red-100 dark:bg-red-950 text-red-600 dark:text-red-400')">
                    {{ entry.kind === 'invoice'
                        ? (entry.action === 'sold' ? 'Sale' : 'Purchase')
                        : (entry.action === 'received' ? 'Received' : 'Sent') }}
                  </span>
                </div>
                <p class="text-xs text-slate-400 mt-0.5">{{ formatDate(entry.target_date) }}</p>
              </div>

              <!-- Amount + running balance + chevron -->
              <div class="flex items-center gap-2 shrink-0">
                <div class="text-right">
                  <p class="text-sm font-bold tabular-nums leading-tight"
                    :class="entry.delta >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'">
                    {{ entry.delta >= 0 ? '+' : '−' }}Rs {{ formatNum(Math.abs(entry.delta)) }}
                  </p>
                  <p class="text-[11px] tabular-nums leading-tight mt-0.5">
                    <span class="text-slate-400 dark:text-slate-500">Bal: </span>
                    <span class="font-medium" :class="entry.runningBalance >= 0 ? 'text-slate-600 dark:text-slate-300' : 'text-red-400 dark:text-red-500'">
                      Rs {{ formatNum(Math.abs(entry.runningBalance)) }}
                    </span>
                  </p>
                </div>
                <svg class="w-4 h-4 text-slate-300 dark:text-slate-600 transition-transform shrink-0"
                  :class="expandedId === entry.id ? 'rotate-180' : ''"
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
                </svg>
              </div>
            </div>

            <!-- Expanded: Invoice -->
            <div v-if="expandedId === entry.id && entry.kind === 'invoice'"
              class="border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/20">

              <!-- Mobile-friendly item cards -->
              <div class="sm:hidden divide-y divide-slate-100 dark:divide-slate-800">
                <div v-for="(item, i) in entry.items" :key="i" class="px-4 py-3 flex items-center justify-between gap-2">
                  <div class="flex items-center gap-2.5 min-w-0">
                    <span class="w-5 h-5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400 text-[10px] font-bold flex items-center justify-center shrink-0">{{ i + 1 }}</span>
                    <div class="min-w-0">
                      <p class="text-sm font-medium text-slate-800 dark:text-white truncate">{{ item.product_name }}</p>
                      <p class="text-xs text-slate-400">{{ item.quantity }} × Rs {{ formatNum(item.rate) }}</p>
                    </div>
                  </div>
                  <p class="text-sm font-bold text-slate-800 dark:text-white shrink-0">Rs {{ formatNum(item.total) }}</p>
                </div>
              </div>

              <!-- Desktop table -->
              <table class="hidden sm:table w-full text-xs">
                <thead>
                  <tr class="border-b border-slate-200 dark:border-slate-700">
                    <th class="text-left px-5 py-2.5 text-slate-400 font-semibold uppercase tracking-wide w-8">#</th>
                    <th class="text-left px-4 py-2.5 text-slate-400 font-semibold uppercase tracking-wide">Product</th>
                    <th class="text-center px-3 py-2.5 text-slate-400 font-semibold uppercase tracking-wide">Qty</th>
                    <th class="text-right px-3 py-2.5 text-slate-400 font-semibold uppercase tracking-wide">Rate</th>
                    <th class="text-right px-5 py-2.5 text-slate-400 font-semibold uppercase tracking-wide">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, i) in entry.items" :key="i"
                    class="border-b border-slate-100 dark:border-slate-800"
                    :class="i % 2 !== 0 ? 'bg-white/70 dark:bg-slate-900/30' : ''">
                    <td class="px-5 py-2.5 text-slate-400">{{ i + 1 }}</td>
                    <td class="px-4 py-2.5 font-medium text-slate-700 dark:text-slate-300">{{ item.product_name }}</td>
                    <td class="px-3 py-2.5 text-center text-slate-500 dark:text-slate-400">{{ item.quantity }}</td>
                    <td class="px-3 py-2.5 text-right text-slate-500 dark:text-slate-400">Rs {{ formatNum(item.rate) }}</td>
                    <td class="px-5 py-2.5 text-right font-semibold text-slate-800 dark:text-white">Rs {{ formatNum(item.total) }}</td>
                  </tr>
                </tbody>
              </table>

              <!-- Total -->
              <div class="flex justify-end px-4 py-3 bg-white/50 dark:bg-slate-900/20">
                <div class="min-w-[180px] space-y-1">
                  <div class="flex justify-between text-xs text-slate-400">
                    <span>Subtotal</span><span class="tabular-nums">Rs {{ formatNum(entry.total) }}</span>
                  </div>
                  <div class="flex justify-between text-sm font-bold border-t border-slate-200 dark:border-slate-700 pt-1.5 tabular-nums"
                    :class="entry.action === 'sold' ? 'text-emerald-600 dark:text-emerald-400' : 'text-blue-600 dark:text-blue-400'">
                    <span>Total</span><span>Rs {{ formatNum(entry.total) }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Expanded: Payment -->
            <div v-if="expandedId === entry.id && entry.kind === 'payment'"
              class="border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/20 px-4 py-4 space-y-3">
              <div class="grid grid-cols-3 gap-3">
                <div class="bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 p-3">
                  <p class="text-[10px] text-slate-400 uppercase tracking-wide mb-1">Amount</p>
                  <p class="text-base font-bold tabular-nums"
                    :class="entry.action === 'received' ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'">
                    Rs {{ formatNum(entry.amount) }}
                  </p>
                </div>
                <div class="bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 p-3">
                  <p class="text-[10px] text-slate-400 uppercase tracking-wide mb-1">Method</p>
                  <p class="text-sm font-semibold text-slate-700 dark:text-slate-300 capitalize">
                    {{ (entry.source || '').replace(/_/g, ' ') }}
                  </p>
                </div>
                <div class="bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 p-3">
                  <p class="text-[10px] text-slate-400 uppercase tracking-wide mb-1">Date</p>
                  <p class="text-sm font-semibold text-slate-700 dark:text-slate-300">{{ formatDate(entry.target_date) }}</p>
                </div>
              </div>
              <p class="text-xs italic text-slate-400 dark:text-slate-500">
                <span class="font-semibold not-italic text-slate-500 dark:text-slate-400">Note:</span>
                {{ props.customer.name }}
                {{ entry.action === 'received' ? 'received' : 'sent' }}
                Rs {{ formatNum(entry.amount) }} via {{ (entry.source || '').replace(/_/g, ' ') }} on {{ formatDate(entry.target_date) }}.
              </p>
            </div>

          </template>
        </template>

        <!-- Empty state -->
        <div v-else class="py-8 text-center border-b border-slate-100 dark:border-slate-800">
          <div class="w-12 h-12 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-3">
            <svg class="w-6 h-6 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"/>
            </svg>
          </div>
          <p class="text-slate-500 dark:text-slate-400 text-sm font-medium">No transactions yet</p>
        </div>

        <!-- Opening balance rows (only shown if non-zero) -->
        <div v-if="financials?.opening_balance_to_receive && financials?.opening_balance_to_receive !== 0" class="flex items-center gap-3 px-4 py-3.5 border-b border-slate-50 dark:border-slate-800/80">
          <div class="w-9 h-9 rounded-xl shrink-0 flex items-center justify-center bg-emerald-100 dark:bg-emerald-950">
            <svg class="w-4 h-4 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <div class="flex-1 min-w-0">
            <span class="text-sm font-semibold text-slate-800 dark:text-white">Opening Balance</span>
            <p class="text-xs text-slate-400 mt-0.5">To Receive</p>
          </div>
          <span class="text-sm font-bold tabular-nums text-emerald-600 dark:text-emerald-400 whitespace-nowrap">
            Rs {{ formatNum(financials?.opening_balance_to_receive) }}
          </span>
        </div>

        <div v-if="financials?.opening_balance_to_give && financials?.opening_balance_to_give !== 0" class="flex items-center gap-3 px-4 py-3.5" :class="(financials?.opening_balance_to_receive && financials?.opening_balance_to_receive !== 0) ? '' : 'border-b border-slate-50 dark:border-slate-800/80'">
          <div class="w-9 h-9 rounded-xl shrink-0 flex items-center justify-center bg-red-100 dark:bg-red-950">
            <svg class="w-4 h-4 text-red-500 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <div class="flex-1 min-w-0">
            <span class="text-sm font-semibold text-slate-800 dark:text-white">Opening Balance</span>
            <p class="text-xs text-slate-400 mt-0.5">To Give</p>
          </div>
          <span class="text-sm font-bold tabular-nums text-red-500 dark:text-red-400 whitespace-nowrap">
            Rs {{ formatNum(financials?.opening_balance_to_give) }}
          </span>
        </div>

      </div>

      <!-- CTA (only shown when user is not logged in) -->
      <div v-if="!isLoggedIn" class="rounded-2xl bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-950/40 dark:to-violet-950/40 border border-indigo-100 dark:border-indigo-900/50 px-5 py-5 text-center">
        <p class="text-sm font-semibold text-slate-700 dark:text-slate-200 leading-snug">
          Want to track invoices &amp; payments<br>like this for your business?
        </p>
        <p class="text-xs text-slate-400 dark:text-slate-500 mt-1.5">
          Keep your accounts clean, organized &amp; shareable.
        </p>
        <a href="/register" target="_blank"
          class="inline-flex items-center gap-2 mt-4 bg-indigo-600 hover:bg-indigo-500 active:scale-95 text-white text-sm font-semibold px-5 py-2.5 rounded-xl shadow-md shadow-indigo-200 dark:shadow-indigo-900/40 transition">
          Try MasterLedger Free
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"/>
          </svg>
        </a>
      </div>

      <!-- Footer -->
      <div class="flex items-center justify-center gap-1.5 pb-8 pt-2">
        <p class="text-xs text-slate-400">Powered by</p>
        <p class="text-xs font-extrabold tracking-tight text-slate-700">Master<span class="text-indigo-600">Ledger</span></p>
      </div>

    </div><!-- end white card -->
  </div><!-- end page -->
</template>

<script setup>
import { ref, onMounted } from 'vue'

const props = defineProps({
  customer: Object,
  is_logged_in: Boolean,
  is_own_customer: Boolean
})

const isLoggedIn = ref(props.is_logged_in)

const financials  = ref(null)
const ledger      = ref([])
const dataLoading = ref(true)

async function fetchData() {
  try {
    const res  = await fetch(`/customers/${props.customer.id}/details/data`, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
    const json = await res.json()
    financials.value = json.financials

    // Sort oldest-first to compute running balance chronologically
    const sorted = (json.ledger || []).slice().sort((a, b) => (a.target_date || '').localeCompare(b.target_date || ''))
    let running = (json.financials?.opening_balance_to_receive || 0) - (json.financials?.opening_balance_to_give || 0)

    const withBalance = sorted.map(entry => {
      const amt = entry.kind === 'invoice' ? (entry.total || 0) : (entry.amount || 0)
      // delta: positive = balance in your favour increases; negative = decreases
      const delta = entry.kind === 'invoice'
        ? (entry.action === 'sold' ? amt : -amt)
        : (entry.action === 'received' ? -amt : amt)
      running += delta
      return { ...entry, delta, runningBalance: running }
    })

    ledger.value = withBalance.reverse() // display newest first
  } finally {
    dataLoading.value = false
  }
}

onMounted(() => {
  const label = props.customer?.shop_name || props.customer?.name || 'Customer'
  document.title = `${label} | History`

  const description = `${props.customer?.shop_name} × ${props.customer?.business_name} · Full transaction history, invoices & payments`
  let meta = document.querySelector('meta[name="description"]')
  if (!meta) {
    meta = document.createElement('meta')
    meta.name = 'description'
    document.head.appendChild(meta)
  }
  meta.content = description

  fetchData()
})

const expandedId = ref(null)
function toggleExpand(id) {
  expandedId.value = expandedId.value === id ? null : id
}

function formatNum(n) {
  return Number(n).toLocaleString('en-PK')
}

function formatDate(d) {
  if (!d) return '—'
  const [y, m, day] = d.split('-')
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  return `${parseInt(day)} ${months[parseInt(m) - 1]} ${y}`
}
</script>
